import React, { useState } from "react";
import styled, { css } from "styled-components";
import theme from "../../assets/theme";
import COLORS from "../../assets/Colors";
import {
  CustomInputField,
  CustomDatePicker,
  CustomTimePicker,
  CustomCheckBoxs,
  StyledMenuItem,
} from "../common/FormInputs";
import { CustomRadioGroup } from "../common/FormInputs";
import {
  useMediaQuery,
  useTheme,
  IconButton,
  InputAdornment,
  TextareaAutosize,
  Switch,
  withStyles,
  FormLabel,
} from "@material-ui/core";
import {
  EventNote,
  RemoveCircleOutline,
  Visibility,
  VisibilityOff,
} from "@material-ui/icons";
import CustomSelect from "../common/CustomSelect/CustomSelect";
import { CustomFileUpload } from "../common/FormInputs";
import CustomSelectEvent from "../common/CustomSelect/CustomSelectEvent";
import MultipleCustomSelectEvent from "../common/CustomSelect/MultipleCustomSelect";
import AWSService from "../../services/AWSService";
import useLoader from "../../hooks/useLoader";
import { useSnackbar } from "notistack";
import { getErrorMessage, getFileName } from "../../helpers/functions";
import { Autocomplete, createFilterOptions } from "@material-ui/lab";
import * as XLSX from "xlsx";

const IOSSwitch = withStyles((theme) => ({
  root: {
    width: 52,
    height: 26,
    padding: 0,
    margin: theme.spacing(1),
  },
  switchBase: {
    padding: 1,
    "&$checked": {
      transform: "translateX(26px)",
      color: "#ffffff",
      "& + $track": {
        backgroundColor: "#52d869",
        opacity: 1,
        border: "none",
      },
    },
    "&$focusVisible $thumb": {
      color: "#52d869",
      border: "6px solid #fff",
    },
  },
  thumb: {
    width: 24,
    height: 24,
  },
  track: {
    borderRadius: 26 / 2,
    border: `1px solid ${theme.palette.grey[400]}`,
    backgroundColor: "#e1e3ee",
    opacity: 1,
    transition: theme.transitions.create(["background-color", "border"]),
  },
  checked: {},
  focusVisible: {},
  disabled: {},
}))(({ classes, error, ...props }) => {
  return (
    <>
      <Switch
        focusVisibleClassName={classes.focusVisible}
        disableRipple
        classes={{
          root: classes.root,
          switchBase: classes.switchBase,
          thumb: classes.thumb,
          track: classes.track,
          checked: classes.checked,
          disabled: classes.disabled,
        }}
        {...props}
      />
      {error && <ErrorText>{error}</ErrorText>}
    </>
  );
});

const FormField = ({
  item,
  handleChange,
  handleBlur,
  values,
  direction,
  setFieldValue,
  index,
  width,
  value,
  name,
  error,
  helperText,
  placeholderText,
  menuItems,
  touched,
  hosts,
  multiple,
  selectedItems,
  hidden,
  accept,
  fileName,
  eventId,
  disabled,
}) => {
  const themes = useTheme();
  const isBigScreen = useMediaQuery(themes.breakpoints.up(640));
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const filter = createFilterOptions();
  const [visibility, setVisibility] = useState(false);

  //S3 upload

  async function s3Upload(file, field, setFieldValue, accept) {
    if (file) {
      try {
        setLoader({ state: true, message: `Uploading file: ${file.name} ...` });
        const payload = {
          key: file.name,
        };
        const MAX_SIZE = 1 * 1024 * 1024;
        if (file.size > MAX_SIZE && accept !== ".mp4") {
          enqueueSnackbar("File size exceeds 1MB. Upload not allowed", {
            variant: "error",
          });
        } else {
          const s3Response = await AWSService.getS3URL(payload);
          if (s3Response.url) {
            const res = await AWSService.uploadToS3(s3Response.url, file);
            setFieldValue(field, res);
          }
          enqueueSnackbar("Successfully Uploaded", { variant: "success" });
        }
      } catch (error) {
        const message = getErrorMessage(error);
        enqueueSnackbar(message, { variant: "error" });
      } finally {
        setLoader({ state: false });
      }
    }
  }

  // Handle changes in the text input
  const handleTextInputChange = (e, name) => {
    const value = e.target.value.trim();
    setFieldValue(name, value);

    // Check if it's a valid hex color
    if (/^#[0-9A-F]{6}$/i.test(value)) {
      setFieldValue(name, value);
    }
    // Check if it's a valid RGB color
    else if (
      /^rgb\(\s*(\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3})\s*\)$/i.test(value)
    ) {
      const match = value.match(
        /^rgb\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3})\)$/i,
      );
      const [r, g, b] = match.slice(1).map(Number);
      if (r <= 255 && g <= 255 && b <= 255) {
        setFieldValue(name, value);
      }
    }
    // Check if it's a valid CSS color name
    else if (isValidColorName(value)) {
      setFieldValue(name, value);
    }
  };

  // Function to validate CSS color names
  const isValidColorName = (colorName) => {
    const s = new Option().style;
    s.color = colorName;
    return s.color !== "";
  };

  // Convert RGB to Hex for the color picker input
  const rgbToHex = (rgb) => {
    const match = rgb?.match(/^rgb\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3})\)$/i);
    if (!match) return rgb;
    const hex = match
      .slice(1)
      .map((num) => Number(num).toString(16).padStart(2, "0"))
      .join("");
    return `#${hex}`;
  };

  const autoHandleChange = (event, newValue, name) => {
    if (typeof newValue === "string") {
      setFieldValue(name, newValue.name);
    } else if (newValue && newValue.inputValue) {
      // Create a new value from the user input
      setFieldValue(name, newValue.inputValue);
    } else {
      if (newValue === null) {
        setFieldValue(name, "");
      } else {
        setFieldValue(name, newValue.name);
      }
    }
  };

  const createNewFilterOptions = (options, params) => {
    const filtered = filter(options, params);

    // Suggest the creation of a new value
    if (params.inputValue !== "") {
      filtered.push({
        inputValue: params.inputValue,
        name: `Add "${params.inputValue}"`,
      });
    }

    return filtered;
  };

  const callAutoRenderOptions = (option) => {
    // Value selected with enter, right from the input
    if (typeof option === "string") {
      return option;
    }
    // Add "xxx" option created dynamically
    if (option.inputValue) {
      return option.inputValue;
    }
    // Regular option
    return option.name;
  };

  const handleFileUpload = async (file, name, setFieldValue) => {
    try {
      const arrayBuffer = await file.arrayBuffer(); // Convert file to ArrayBuffer
      const workbook = XLSX.read(arrayBuffer, { type: "array" }); // Read the workbook
      const sheetName = workbook.SheetNames[0]; // Get the first sheet
      const sheet = workbook.Sheets[sheetName]; // Access the first sheet
      const data = XLSX.utils.sheet_to_json(sheet, { header: 1 }); // Convert to 2D array
      setFieldValue(name, [file?.name, ...data.flat()]);
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <Container field={item.type === "switch"}>
      <LabelContainer field={item.type === "switch"} width={width}>
        <FormLabel
          style={{
            fontFamily: theme.fonts.primaryFontSemiBold,
            fontSize: 14,
            color: item?.labelColor ? item?.labelColor : COLORS.TRANSCRIPT_GRAY,
            display: item?.labelHidden ? "none" : "inline",
          }}
        >
          {item.label}
          {item.required && <span className="required-star"> *</span>}
        </FormLabel>
      </LabelContainer>
      <FieldContainer>
        {item.type === "switch" ? (
          <IOSSwitch
            checked={value}
            onChange={handleChange}
            name={name ? name : item.name}
            error={error}
            disabled={disabled}
          />
        ) : item.type === "text" ? (
          <CustomInputField
            style={{
              marginTop: 6,
              marginBottom: 10,
              display: hidden ? "none" : "",
            }}
            variant="outlined"
            name={name}
            value={value}
            placeholder={item.placeholder}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched && error}
            helperText={touched && helperText}
            hidden={hidden}
            disabled={disabled}
          />
        ) : item.type === "radio" ? (
          <CustomRadioGroup
            direction={direction ? direction : "column"}
            radioInputs={menuItems ? menuItems : item.menuItems}
            handleChange={handleChange}
            handleBlur={handleBlur}
            name={name ? name : item.name}
            value={value}
            error={touched && error}
            helperText={touched && helperText}
            {...{ values, setFieldValue, eventId }}
          />
        ) : item.type === "typeSelect" ? (
          <CustomSelectEvent
            style={{ width: isBigScreen ? "250px" : "220px", marginTop: 10 }}
            id={name}
            menuItemValues={menuItems}
            name={name}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            multiple={multiple}
            selectedItems={selectedItems}
            values={values}
            hosts={hosts}
            error={touched && error}
            helperText={touched && helperText}
          />
        ) : item.type === "multipleSelect" ? (
          <MultipleCustomSelectEvent
            style={{ width: isBigScreen ? "250px" : "220px", marginTop: 10 }}
            id={name}
            menuItemValues={menuItems}
            name={name}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            multiple={multiple}
            selectedItems={selectedItems}
            values={values}
            hosts={hosts}
            error={error}
            helperText={helperText}
          />
        ) : item.type === "textarea" ? (
          <Relative>
            <CustomTextArea
              {...{ isBigScreen }}
              variant="outlined"
              name={name}
              maxRows={5}
              minRows={5}
              value={value}
              error={error}
              helperText={helperText}
              onChange={handleChange}
            />
            {helperText && <HelperText>{helperText}</HelperText>}
          </Relative>
        ) : item.type === "select" ? (
          <CustomSelect
            style={{ width: isBigScreen ? "250px" : "220px", marginTop: 6 }}
            menuItemValues={
              menuItems ? menuItems : [{ label: "None", value: "none" }]
            }
            value={value ? value : values[item.name]}
            name={name ? name : item.name}
            onChange={handleChange}
            multiple={multiple}
            selectedItems={selectedItems}
            error={touched && error}
            helperText={touched && helperText}
            placeholder={item.placeholder}
          />
        ) : item.type === "upload" ? (
          <div style={{ display: "flex" }}>
            <CustomFileUpload
              width={isBigScreen ? 250 : 230}
              setFile={(file) => s3Upload(file, name, setFieldValue, accept)}
              style={{
                marginTop: 3,
              }}
              accept={accept ? accept : "image/*"}
              file={{
                name: getFileName(value),
              }}
              error={error}
              fileUrl={value}
            />
            {value ? (
              <div
                style={{
                  cursor: "pointer",
                  width: "auto",
                  marginLeft: "8px",
                  marginTop: "10px",
                }}
                onClick={() => setFieldValue(name, "")}
              >
                <RemoveCircleOutline
                  style={{
                    color: COLORS.LOGOUT_RED,
                  }}
                />{" "}
              </div>
            ) : null}
          </div>
        ) : item.type === "uploads" ? (
          <CustomFileUpload
            width={isBigScreen ? 250 : 230}
            setFile={(file) =>
              handleFileUpload(file, name, setFieldValue, fileName)
            }
            style={{
              marginTop: 3,
            }}
            accept={accept ? accept : "image/*"}
            file={{
              name: value?.length ? value[0] : "",
            }}
            error={error}
            fileUrl={value[0]}
          />
        ) : item.type === "color" ? (
          <div key={index} style={{ display: "flex" }}>
            <input
              className="color-picker"
              variant="outlined"
              type="color"
              key={index}
              value={rgbToHex(value ? value : values[item.name])} // Convert to hex for the color picker
              onChange={(e) =>
                setFieldValue(name ? name : item.name, e.target.value)
              }
            />
            <CustomInputField
              variant="outlined"
              type="text"
              name={name}
              value={value ? value : values[item.name]}
              onChange={(e) =>
                handleTextInputChange(e, name ? name : item.name)
              }
              placeholder="Enter #hex, rgb(), or color name"
              style={{
                width: isBigScreen ? "215px" : "180px",
              }}
            />
          </div>
        ) : item.type === "checkbox" ? (
          <CustomCheckBoxs
            direction={direction ? direction : "column"}
            radioInputs={item.checkItems}
            handleChange={handleChange}
            {...{ values, value, name, setFieldValue }}
          />
        ) : item.type === "autoSelect" ? (
          <Autocomplete
            id="size-small-outlined"
            size="small"
            value={value}
            onChange={(event, newValue) =>
              autoHandleChange(event, newValue, name)
            }
            filterOptions={(options, params) =>
              createNewFilterOptions(options, params)
            }
            open={open}
            onOpen={() => {
              setOpen(true);
            }}
            onClose={() => {
              setOpen(false);
            }}
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            options={menuItems}
            getOptionLabel={(option) => callAutoRenderOptions(option)}
            ListboxComponent={(props) => (
              <ul style={{ border: "1px solid #e1e3ee" }} {...props}>
                {props?.children.map((element) => (
                  <StyledMenuItem {...element.props}>
                    {element.props.children}
                  </StyledMenuItem>
                ))}
              </ul>
            )}
            style={{ marginTop: 6, marginBottom: 10 }}
            renderInput={(params) => (
              <CustomInputField
                style={{
                  width: isBigScreen ? "250px" : "auto",
                }}
                {...params}
                error={error}
                helperText={helperText}
                variant="outlined"
              />
            )}
          />
        ) : item.type === "time" ? (
          <CustomTimePicker
            className={"input-white"}
            clearable
            inputVariant="outlined"
            ampm={true}
            placeholder={placeholderText}
            error={touched && error}
            helperText={touched && helperText}
            value={value}
            disabled={item.disabled}
            onChange={(e) => setFieldValue(name, e)}
            style={{ marginTop: 6 }}
          />
        ) : item.type === "duration" ? (
          <CustomTimePicker
            className={"input-white"}
            clearable
            inputVariant="outlined"
            ampm={false}
            placeholder={placeholderText}
            error={touched && error}
            helperText={touched && helperText}
            value={value}
            onChange={(e) => setFieldValue(name, e)}
            style={{ marginTop: 6 }}
          />
        ) : item.type === "number" ? (
          <CustomInputField
            style={{
              marginTop: 6,
              marginBottom: 10,
              width: isBigScreen ? "250px" : "180px",
            }}
            variant="outlined"
            type="number"
            name={name}
            value={value}
            placeholder={item.placeholder}
            onChange={handleChange}
            onBlur={handleBlur}
            error={error}
            helperText={helperText}
            hidden={hidden}
          />
        ) : item.type === "password" ? (
          <CustomInputField
            // className={"input-white"}
            variant="outlined"
            id={name}
            name={name}
            value={value}
            placeholder={item.placeholder}
            onChange={handleChange}
            onBlur={handleBlur}
            error={error}
            helperText={helperText}
            hidden={hidden}
            row={4}
            autoComplete={"new-password"}
            type={
              item.type === "password"
                ? visibility
                  ? "text"
                  : item.type
                : item.type
            }
            style={{ marginBottom: 10, marginTop: 6 }}
            // disabled={disabled}
            InputProps={{
              endAdornment: (
                <>
                  {item.type === "password" ? (
                    <InputAdornment position="end">
                      <IconButton
                        color="inherit"
                        aria-label="toggle password visibility"
                        onClick={() => setVisibility(!visibility)}
                        edge="end"
                      >
                        {visibility ? (
                          <Visibility
                            style={{ fontSize: 18, fill: "#00aeef" }}
                          />
                        ) : (
                          <VisibilityOff
                            style={{ fontSize: 18, fill: "#00aeef" }}
                          />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ) : null}
                </>
              ),
              // autoComplete: "off",
              fullWidth: true,
            }}
            inputProps={{
              style: {
                height: 3,
                width: "100%",
                background: "transparent",
                borderRadius: "10px",
                // WebkitBoxShadow: "0 0 0 200px white inset",
              },
            }}
            min={0}
          />
        ) : (
          <CustomDatePicker
            inputVariant="outlined"
            format={"DD/MM/YYYY"}
            name={name}
            value={value}
            placeholder={item.placeholder}
            minDate={new Date()}
            // views={["year", "month", "date"]}
            onChange={(e) => setFieldValue(name, e)}
            style={{ marginTop: 6, width: isBigScreen ? "250px" : "230px" }}
            error={touched && error}
            disabled={item.disabled}
            helperText={touched && helperText}
            InputProps={{
              endAdornment: (
                <>
                  <InputAdornment position="end">
                    <IconButton color="inherit" edge="end">
                      <EventNote style={{ fill: "#00aeef" }} />
                    </IconButton>
                  </InputAdornment>
                </>
              ),
            }}
          />
        )}
      </FieldContainer>
    </Container>
  );
};

export default FormField;

const Relative = styled.div`
  position: relative;
`;

const CustomTextArea = styled(TextareaAutosize)`
  width: 100%;
  min-width: 100%;
  max-width: 100%;
  font-size: 14px;
  color: ${COLORS.COLOR_DARK} !important;
  font-family: ${theme.fonts.primaryFontSemiBold};
  /* min-height: 89px; */
  border-radius: 6px;
  border: solid 0.5px #e1e3ee;
  background-color: #ffffff;
  outline: none;
  padding: 15px;
  :hover {
    border: solid 0.5px black;
  }
  ${({ error }) =>
    error &&
    css`
      border: solid 0.5px #f44336;
      :hover {
        border: solid 0.5px #f44336;
      }
    `}
`;
export const HelperText = styled.div`
  position: absolute;
  left: 3px;
  font-size: 10px;
  font-family: openSans-SemiBold, sans-serif;
  color: #f44336;
`;

export const ErrorText = styled.div`
  position: absolute;
  font-size: 10px;
  font-family: openSans-SemiBold, sans-serif;
  color: #f44336;
`;

export const ButtonContainer = styled.div`
  display: flex;
  margin-top: 25px;
`;

export const FormControl = styled.div`
  margin-bottom: 13px;
  @media ${theme?.breakpoints?.sm_up} {
    width: 100%;
  }
`;

const Container = styled.div`
  display: ${({ field }) => (field ? "flex" : "block")};
`;

const LabelContainer = styled.div`
  width: ${({ field, width }) => (field ? (width ? width : "180px") : "auto")};
  margin-top: ${({ field }) => (field ? "10px" : "0px")};
`;

const FieldContainer = styled.div``;
